/**
 * CR-6679: Acknowledgement Upload History component.
 *
 * Displays the history of acknowledgement file uploads that touched a specific PO.
 * Similar pattern to BatchHistory - shows upload details with download capability.
 */
import { useEffect, useState } from 'react';
import { Upload, Loader2, AlertCircle, Calendar, User, FileText, Download, CheckCircle, XCircle, Clock } from 'lucide-react';
import { purchasingAPI, AckUpload } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface AckUploadHistoryProps {
  /** The PO ID to fetch acknowledgement uploads for */
  poId: number;
}

/**
 * Displays acknowledgement upload history for a specific PO.
 * Shows uploads that processed lines from this PO, with download links.
 */
export default function AckUploadHistory({ poId }: AckUploadHistoryProps) {
  const [uploads, setUploads] = useState<AckUpload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatDateTime } = useFormatters();

  useEffect(() => {
    fetchAckUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  const fetchAckUploads = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getAckUploadsForPO(poId);
      setUploads(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch ack upload history:', err);
      setError(getApiErrorMessage(err, 'Failed to load acknowledgement upload history'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (upload: AckUpload) => {
    if (upload.file_url) {
      window.open(upload.file_url, '_blank');
      toast.success(`Downloading ${upload.original_filename}`);
    } else {
      toast.error('File not available for download');
    }
  };

  const getStatusBadge = (status: AckUpload['status']) => {
    const statusConfig: Record<string, { label: string; icon: typeof CheckCircle; className: string }> = {
      confirmed: { label: 'Confirmed', icon: CheckCircle, className: 'bg-green-100 text-green-800 border-green-200' },
      preview: { label: 'Preview Only', icon: Clock, className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
      cancelled: { label: 'Cancelled', icon: XCircle, className: 'bg-red-100 text-red-800 border-red-200' },
    };

    const config = statusConfig[status] || statusConfig['preview'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getUploadTypeBadge = (uploadType: AckUpload['upload_type']) => {
    if (uploadType === 'customer') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Customer Upload
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        Supplier Upload
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading upload history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-600 text-sm">{error}</p>
        <button
          onClick={fetchAckUploads}
          className="mt-3 px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (uploads.length === 0) {
    return (
      <div className="text-center py-8 border rounded-lg bg-muted/10">
        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground text-sm font-medium">No acknowledgement uploads found</p>
        <p className="text-xs text-muted-foreground mt-1">
          Upload history will appear here when acknowledgements are imported for this PO
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Upload className="h-4 w-4" />
        <span>{uploads.length} acknowledgement upload{uploads.length !== 1 ? 's' : ''} found</span>
      </div>

      {/* Upload Cards */}
      <div className="space-y-3">
        {uploads.map((upload) => (
          <div
            key={upload.id}
            className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow"
          >
            <div className="flex items-start justify-between gap-4">
              {/* Left side - File info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium text-sm truncate" title={upload.original_filename}>
                    {upload.original_filename}
                  </span>
                  {getStatusBadge(upload.status)}
                  {getUploadTypeBadge(upload.upload_type)}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDateTime(upload.created_at)}</span>
                  </div>
                  {upload.uploaded_by_name && (
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{upload.uploaded_by_name}</span>
                    </div>
                  )}
                  {upload.formatted_file_size && (
                    <div className="text-muted-foreground">
                      Size: {upload.formatted_file_size}
                    </div>
                  )}
                  {upload.target_supplier_name && (
                    <div className="text-muted-foreground">
                      Supplier: {upload.target_supplier_name}
                    </div>
                  )}
                </div>

                {/* Row counts */}
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="text-muted-foreground">
                    Total rows: <span className="font-medium text-foreground">{upload.rows_total}</span>
                  </span>
                  <span className="text-green-700">
                    Acknowledged: <span className="font-medium">{upload.rows_acknowledged}</span>
                  </span>
                  {/* CR-7328: acknowledged counts true acknowledgements only, so the
                      rows that raised a change request are shown in their own right. */}
                  {(upload.rows_change_proposed ?? 0) > 0 && (
                    <span className="text-blue-700">
                      Change requests raised: <span className="font-medium">{upload.rows_change_proposed}</span>
                    </span>
                  )}
                  {(upload.rows_error ?? 0) > 0 && (
                    <span className="text-red-600">
                      Errors: <span className="font-medium">{upload.rows_error}</span>
                    </span>
                  )}
                  {(upload.rows_discrepancy ?? 0) > 0 && (
                    <span className="text-yellow-600">
                      Discrepancies: <span className="font-medium">{upload.rows_discrepancy}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Right side - Download button */}
              {upload.file_url && (
                <button
                  onClick={() => handleDownload(upload)}
                  className="p-2 hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                  title="Download original file"
                >
                  <Download className="h-4 w-4 text-primary" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
